import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import cx from 'classnames';
import PropTypes from 'prop-types';

import { SessionStorageManager, SiteConfigManager, useCasinoNewState } from '@atlas/core';
import { Button, Icon, Label } from '@atlas/ui';

import { CDN_TYPE_GAME, imageUrlCDN } from 'common/utils/utils';
import { useQuickDepositPostHog } from 'hooks/usePostHog';
import { QUICK_DEPOSIT_POPUP_TITLE_TYPE, REDIRECT_DATA_AFTER_DEPOSIT } from 'src/common/constants';
import QuickDepositForm, {
  BUTTON_POSITION,
} from 'src/common/Popup/QuickDepositPopup/QuickDepositForm';

const InsufficientBalance = ({ game, onGoBack, setPaymentUrl }) => {
  const { handleCloseClick, handleDepositStart, handleDepositSuccess } = useQuickDepositPostHog({
    afterDelay: null,
    onCloseClick: onGoBack,
  });
  const demoButtonStateForSignedInUsers = SiteConfigManager.getConfig(
    'config.config.casino.mobile_demo_logged',
    false,
  );
  const history = useHistory();
  const { pathname, search } = useLocation();
  // Replace with new casino service after merge FT-7454
  const {
    addToFavorites,
    favoriteGames: { data: favoriteGames },
    identifyFavoriteGames,
    removeFromFavorites,
  } = useCasinoNewState();
  const [showImage, setShowImage] = useState(true);
  const { iconUrl, id, isDemoAvailable, name, providerName } = game;

  const isFavoriteGame = useMemo(() => favoriteGames.includes(id), [favoriteGames, id]);

  const onOpenDemo = useCallback(() => {
    handleCloseClick(false);
    const params = new URLSearchParams(search);
    params.set('demo', 'true');

    history.push(`${pathname}?${params.toString()}`);
  }, [handleCloseClick, history, pathname, search]);

  const onToggleFavorite = useCallback(() => {
    if (isFavoriteGame) {
      removeFromFavorites(id);
    } else {
      addToFavorites(id);
    }
  }, [addToFavorites, id, isFavoriteGame, removeFromFavorites]);

  useEffect(() => {
    identifyFavoriteGames([id]);
  }, [id, identifyFavoriteGames]);

  const setRedirectUrl = useCallback(() => {
    const data = {
      expirationTime: Date.now() + 2 * 3600 * 1000,
      url: `${pathname}${search}`,
    };

    SessionStorageManager.setItem(REDIRECT_DATA_AFTER_DEPOSIT, JSON.stringify(data));
  }, [pathname, search]);

  const image = iconUrl ? imageUrlCDN(iconUrl, CDN_TYPE_GAME) : null;

  return (
    <div className="insufficient-balance">
      <div className="insufficient-balance__header">
        <div className="insufficient-balance__header-info">
          <button className="back-to-container" onClick={handleCloseClick}>
            <Icon className="icon-back" name="back" />
          </button>
          <div>
            <div className="game-name">{name}</div>
            <div className="game-provider">{providerName}</div>
          </div>
        </div>
        <div className="insufficient-balance__header-buttons">
          {isDemoAvailable && demoButtonStateForSignedInUsers && (
            <Button
              className="demo-button"
              icon="arrow-right"
              onClick={onOpenDemo}
              size="small"
              type="secondary"
            >
              <Label message="demo" />
            </Button>
          )}
          <Button
            className={cx('favorites-button', { 'is-active': isFavoriteGame })}
            icon="star"
            onClick={onToggleFavorite}
            size="small"
            type="secondary"
          />
        </div>
      </div>
      <div className="insufficient-balance__game-image">
        {image && showImage ? (
          <img
            alt={name}
            className="bg-image"
            fetchPriority="auto"
            loading="lazy"
            onError={() => setShowImage(false)}
            role="presentation"
            src={image}
          />
        ) : (
          <div className="default-image">
            <Icon height={108} name="casino-coin" width={108} />
          </div>
        )}
        <div className="insufficient-balance__game-image-text">
          <Label className="block-title" message="insufficient_funds_title" />
          <Label className="block-message" message="insufficient_funds_message" />
        </div>
      </div>
      <div className="insufficient-balance__form">
        <QuickDepositForm
          buttonPosition={BUTTON_POSITION.BOTTOM}
          closeQRCodeOnSuccess
          onDepositStart={handleDepositStart}
          onDepositSuccess={handleDepositSuccess}
          setPaymentUrl={setPaymentUrl}
          setRedirectUrl={setRedirectUrl}
          titleType={QUICK_DEPOSIT_POPUP_TITLE_TYPE.INSUFFICIENT}
        />
      </div>
    </div>
  );
};

InsufficientBalance.propTypes = {
  game: PropTypes.shape({
    iconUrl: PropTypes.string,
    id: PropTypes.number.isRequired,
    isDemoAvailable: PropTypes.bool.isRequired,
    name: PropTypes.string.isRequired,
    providerName: PropTypes.string.isRequired,
  }).isRequired,
  onGoBack: PropTypes.func.isRequired,
  setPaymentUrl: PropTypes.func.isRequired,
};

export default InsufficientBalance;
